import { ArrowRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ComponentsResponse, GraphComponent } from '@/services/api/graph'
import { SectionHeader } from '../primitives/SectionHeader'
import { buildClusterLabelMap } from '../selectors'
import { ClusterDetailPanel } from './ClusterDetailPanel'
import { ClusterListItem } from './ClusterListItem'

export interface ClusterExplorerProps {
  caseId: string
  data: ComponentsResponse | null
  onOpenInGraph?: (component: GraphComponent, label: string) => void
  onSeeAll?: () => void
}

function storageKey(caseId: string) {
  return `insights:cluster:${caseId}`
}

export function ClusterExplorer({ caseId, data, onOpenInGraph, onSeeAll }: ClusterExplorerProps) {
  const components = data?.components ?? []
  const sorted = useMemo(() => [...components].sort((a, b) => b.size - a.size), [components])
  const largestId = sorted[0]?.comp_id ?? null
  const labelMap = useMemo(() => buildClusterLabelMap(data), [data])

  const [selectedId, setSelectedId] = useState<number | null>(() => {
    if (typeof window === 'undefined') return largestId
    const stored = window.localStorage.getItem(storageKey(caseId))
    const parsed = stored ? Number.parseInt(stored, 10) : null
    return parsed != null && !Number.isNaN(parsed) ? parsed : largestId
  })

  useEffect(() => {
    if (sorted.length === 0) return
    const exists = sorted.some(c => c.comp_id === selectedId)
    if (!exists) setSelectedId(largestId)
  }, [sorted, selectedId, largestId])

  const handleSelect = useCallback(
    (compId: number) => {
      setSelectedId(compId)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey(caseId), String(compId))
      }
    },
    [caseId]
  )

  const selected = sorted.find(c => c.comp_id === selectedId) ?? sorted[0] ?? null

  if (sorted.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <SectionHeader
          title="Cluster explorer"
          subLabel="Select a cluster to inspect its composition"
          guide={{
            to: '/docs/investigation-analysis#graph-clusters',
            hint: 'Communities detected by Louvain over the case graph.',
          }}
        />
        <div className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center">
          <p className="text-xs text-[var(--muted-foreground)]">No clusters yet.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
      <SectionHeader
        title="Cluster explorer"
        subLabel="Select a cluster to inspect its composition"
        guide={{
          to: '/docs/investigation-analysis#graph-clusters',
          hint: 'Sample entities and type breakdown for the selected cluster.',
        }}
        actions={
          onSeeAll ? (
            <button
              type="button"
              onClick={onSeeAll}
              className="text-xs text-[var(--primary)] font-medium hover:underline inline-flex items-center gap-1"
            >
              See all {sorted.length} clusters <ArrowRight className="size-3" />
            </button>
          ) : null
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3">
        <div className="space-y-1 max-h-48 overflow-y-auto pr-1 max-md:rounded-lg max-md:border max-md:border-[var(--border)]/70 max-md:p-1 md:max-h-80">
          {sorted.map(c => (
            <ClusterListItem
              key={c.comp_id}
              component={c}
              label={labelMap.get(c.comp_id) ?? `Cluster ${c.comp_id}`}
              active={c.comp_id === selected?.comp_id}
              onSelect={() => handleSelect(c.comp_id)}
            />
          ))}
        </div>
        {selected && (
          <ClusterDetailPanel
            component={selected}
            label={labelMap.get(selected.comp_id) ?? `Cluster ${selected.comp_id}`}
            onOpenInGraph={() =>
              onOpenInGraph?.(
                selected,
                labelMap.get(selected.comp_id) ?? `Cluster ${selected.comp_id}`
              )
            }
          />
        )}
      </div>
    </div>
  )
}
